import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { apiService } from './services/api';
import { Company } from './types';
import CompanySelector from './components/CompanySelector';
import CompanyDetail from './pages/CompanyDetail';
import Login from './pages/Login';
import { isAuthenticated, getCurrentUser, signOut, User } from './utils/auth';
import './App.css';

// メインページコンポーネント
const MainPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState<Error | null>(null);

  const handleCompanySelect = (company: Company) => {
    // 証券コードで詳細ページに遷移
    window.location.href = `/company/${company.security_code}`;
  };

  const handleCompanySearch = async (query: string) => {
    if (!query.trim()) {
      setCompanies([]);
      setSearchQuery('');
      return;
    }

    setSearchQuery(query);
    setCompaniesLoading(true);
    setCompaniesError(null);

    try {
      console.log('Manual search:', query);
      const result = await apiService.searchCompanies(query);
      console.log('Manual search result:', result);
      setCompanies(result);
    } catch (error) {
      console.error('Manual search error:', error);
      setCompaniesError(error as Error);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };


  // エラーメッセージ統合とログ出力
  const error = companiesError;
  const loading = companiesLoading;
  
  // デバッグ情報
  console.log('App state:', {
    searchQuery,
    companiesCount: companies.length,
    companiesError,
    loading
  });

  return (
    <>
      <header className="App-header">
        <h1>財務分析ダッシュボード</h1>
        <p>日本企業の財務データ分析・可視化システム</p>
      </header>

      <main className="App-main">
        {error && (
          <div className="error-message">
            <p>⚠️ {error.message || 'データの取得に失敗しました'}</p>
          </div>
        )}

        <CompanySelector
          companies={companies}
          onCompanySelect={handleCompanySelect}
          onCompanySearch={handleCompanySearch}
          loading={loading}
          searchQuery={searchQuery}
        />
      </main>
    </>
  );
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 認証状態を確認
    if (isAuthenticated()) {
      const currentUser = getCurrentUser();
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData: User) => {
    setUser(userData);
  };

  const handleLogout = () => {
    signOut();
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        読み込み中...
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="App">
        <header style={{ 
          padding: '1rem', 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1>財務分析システム</h1>
          <div>
            <span style={{ marginRight: '1rem' }}>
              ようこそ、{user.email}さん
            </span>
            <button 
              onClick={handleLogout}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ログアウト
            </button>
          </div>
        </header>
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/company/:securityCode" element={<CompanyDetail />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
